import * as React from "react";
import { useMemo } from "react";

import { type User, useAuthStore } from "@/stores/auth/auth-store";

import { loginWithUserData as loginService, logout as logoutService, refresh as refreshService } from "./auth-service";

export interface AuthContext {
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  user: User | null;
  refreshToken: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContext | null>(null);

export function AuthProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const { isAuthenticated, user } = useAuthStore();
  const login = React.useCallback((email: string, password: string) => loginService(email, password), []);

  const refreshToken = React.useCallback(() => refreshService(), []);

  const logout = React.useCallback(() => {
    logoutService();
    return Promise.resolve();
  }, []);

  const contextValue = useMemo(
    () => ({
      isAuthenticated,
      user,
      login,
      logout,
      refreshToken,
    }),
    [isAuthenticated, user, login, logout, refreshToken]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
